import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Mail, KeyRound, CheckCircle, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code+newpass
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
    } catch {
      setError('Failed to process request. Please try again.');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, token: code, new_password: newPassword });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid or expired code. Please try again.');
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-sm w-full">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Password Reset!</h2>
        <p className="text-sm text-gray-500 mb-6">Your password has been updated. You can now sign in.</p>
        <Button onClick={() => navigate('/admin-login')} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
          Go to Sign In
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 text-lg font-black mb-6">
            <span className="text-orange-500">EXPO</span><span className="text-gray-900">STAND</span>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {step === 1 ? 'Forgot Password?' : 'Enter Reset Code'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {step === 1
              ? 'Enter your email to receive a reset code.'
              : `We sent a reset code to ${email}. Check your inbox.`
            }
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Step indicators */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2].map(s => (
              <div key={s} className={`flex-1 h-1 rounded-full transition-all ${s <= step ? 'bg-orange-500' : 'bg-gray-200'}`} />
            ))}
          </div>

          {step === 1 ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="pl-9 h-11"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send Reset Code
              </Button>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              {/* Email sent info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600 shrink-0" />
                  <p className="text-sm text-blue-800 font-medium">Check your inbox at <span className="font-bold">{email}</span></p>
                </div>
                <p className="text-xs text-blue-600 mt-1 ml-6">Enter the 6-digit code from the email below.</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Reset Code</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    placeholder="6-digit code"
                    required
                    maxLength={6}
                    className="pl-9 h-11 text-center text-lg font-bold tracking-widest"
                    data-testid="forgot-code-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">New Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    required
                    minLength={6}
                    className="h-11 pr-10"
                    data-testid="forgot-newpass-input"
                  />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Confirm Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  required
                  className="h-11"
                  data-testid="forgot-confirm-input"
                />
              </div>
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="forgot-reset-btn"
                className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Reset Password
              </Button>
              <button type="button" onClick={() => { setStep(1); setError(null); }}
                className="w-full text-xs text-gray-500 hover:text-gray-700 transition-colors">
                ← Try a different email
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-4">
          <Link to="/admin-login" className="text-sm text-gray-500 hover:text-orange-600 flex items-center justify-center gap-1.5 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
